import { ReadableStream } from '@yume-chan/stream-extra';

export const RangeReadableStream = async (
  url: string,
  chunkSize: number = 1024 * 1024 * 5,
  options: RequestInit = {},
): Promise<{ size: number; stream: ReadableStream<Uint8Array> }> => {
  let position: number = 0;
  let aborted: boolean = false;

  // First, make a HEAD request to get the total file size
  const headResponse = await fetch(url, { method: 'HEAD', ...options });

  if (!headResponse.ok) {
    throw new Error(`Failed to fetch file info: ${headResponse.status} ${headResponse.statusText}`);
  }
  const acceptRanges = headResponse.headers.get('accept-ranges');
  if (acceptRanges !== 'bytes') {
    // Backup strategy: download the whole file
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Content-Type': 'application/octet-stream',
        'cache-control': 'no-cache',
      },
    });
    if (!response.ok || response.body === null) {
      throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
    }
    return {
      size: parseInt(headResponse.headers.get('content-length') || '0', 10),
      stream: headResponse.body as ReadableStream<Uint8Array>,
    };
  }

  const contentLength = parseInt(headResponse.headers.get('content-length') || '0', 10);

  if (!contentLength) {
    throw new Error('Content length is unknown, cannot perform range requests');
  }

  // Create and return a ReadableStream
  const stream = new ReadableStream<Uint8Array>({
    async start(_) {},
    async pull(controller) {
      try {
        if (aborted || contentLength === null || position >= contentLength) {
          controller.close();
          return;
        }

        const end = Math.min(position + chunkSize - 1, contentLength - 1);

        const rangeOptions = {
          ...options,
          headers: {
            ...options.headers,
            Range: `bytes=${position}-${end}`,
            'cache-control': 'no-cache',
          },
        };

        const response = await fetch(url, rangeOptions);

        if (!response.ok && response.status !== 206) {
          throw new Error(`Failed to fetch chunk: ${response.status} ${response.statusText}`);
        }

        const chunk = await response.arrayBuffer();
        controller.enqueue(new Uint8Array(chunk));

        position = end + 1;
      } catch (error) {
        console.error('error', error);
        controller.error(error);
      }
    },

    cancel() {
      aborted = true;
    },
  });
  return { size: contentLength || 0, stream };
};
